"use client"
import { formatString } from "@/lib/formatString";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { useRef, useState, useEffect } from "react";

export function VNTable({ blok }) {
    const scrollContainerRef = useRef(null);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);
    const cellRefs = useRef([]);
    const [rowHeight, setRowHeight] = useState(null);

    const checkScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftButton(scrollLeft > 0);
            setShowRightButton(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            checkScroll();
            scrollContainer.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            }
        };
    }, []);

    useEffect(() => {
        if (cellRefs.current.length) {
            const heights = cellRefs.current.map(ref => ref?.offsetHeight || 0);
            setRowHeight(Math.max(...heights));
        }
    }, [blok]);

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 300;
            const currentScroll = scrollContainerRef.current.scrollLeft;
            const newScroll = direction === "left" 
                ? currentScroll - scrollAmount 
                : currentScroll + scrollAmount;
            
            scrollContainerRef.current.scrollTo({
                left: newScroll,
                behavior: "smooth"
            });
        }
    };

    return (
        <div className="relative max-w-7xl w-full">
            <div 
                ref={scrollContainerRef}
                className="w-full text-center lg:text-left px-4 lg:px-0 overflow-x-scroll hide-scrollbar"
                style={{
                    backgroundSize: "cover",
                    backgroundRepeat: "repeat",
                }}
            >
                <div className="mb-20 min-w-[1322px] lg:min-w-7xl w-7xl lg:w-7xl max-w-[1322px] lg:max-w-7xl p-[5px] flex flex-col rounded-[55px] bg-gradient-to-r from-[#5D2AE780] via-[#EF7B2280] to-[#D259A680] relative">
                    {/** Table Header */}
                    <div className="rounded-t-[55px] flex justify-around bg-gradient-to-r from-[#5A27E7] to-[#9A509D] pt-[45px] pb-[30px] flex-1 text-[22px]">
                        {blok?.TableColumn?.map((col, index) => {
                            return <div key={`zd-table-head-${index}`} className="flex-1 text-center font-bold font-aileron" style={{ color: "#fff" }}>{col.headline}</div>
                        })}
                    </div>
                    {/** Table Body */}
                    {
                        (() => {
                            const cvssIndex = blok?.TableColumn?.findIndex(col => col.headline === "CVSS Score");
                            const riskIndex = blok?.TableColumn?.findIndex(col => col.headline === "Risk Index");
                            const getScoreColor = (score) => {
                                if (score >= 8) return "#A10E1B";
                                if (score >= 6) return "#FF7B7B";
                                if (score >= 4) return "#FFB84D";
                                if (score >= 2) return "#FFD966";
                                return "#D3D7DF";
                            };
                            return (
                                <div className="w-full" style={{ display: "table" }}>
                                    <div style={{ display: "table-row" }}>
                                        <div className="flex justify-around flex-1 text-[18px] items-stretch">
                                            {blok?.TableColumn?.map((col, index) => {
                                                let className = `flex-1 flex items-center justify-center text-center font-bold font-aileron`;
                                                if (index === 0) className += " rounded-bl-[55px]";
                                                if (index === blok?.TableColumn?.length - 1) className += " rounded-br-[55px]";
                                                if (index % 2 === 0) className += " bg-[#E8DFF8]";
                                                else className += " bg-[#f2EFFD]";

                                                let content = col.lead;

                                                if (index === cvssIndex) {
                                                    const score = parseFloat(col.lead);
                                                    content = (
                                                        <span
                                                            style={{
                                                                border: `2px solid ${getScoreColor(score)}`,
                                                                borderRadius: "6px",
                                                                padding: "2px 12px",
                                                                fontWeight: 600,
                                                                fontSize: "1.1em",
                                                                background: "#fff"
                                                            }}
                                                        >
                                                            {col.lead}
                                                        </span>
                                                    );
                                                }

                                                if (index === riskIndex) {
                                                    const score = parseFloat(col.lead);
                                                    content = (
                                                        <span
                                                            style={{
                                                                background: getScoreColor(score),
                                                                color: "#fff",
                                                                borderRadius: "6px",
                                                                padding: "2px 12px",
                                                                fontWeight: 600,
                                                                fontSize: "1.1em"
                                                            }}
                                                        >
                                                            {col.lead}
                                                        </span>
                                                    );
                                                }

                                                return (
                                                    <div
                                                        key={`zd-table-head-${index}`}
                                                        className={className + " min-h-[100px]"}
                                                        ref={el => (cellRefs.current[index] = el)}
                                                        style={rowHeight ? { height: rowHeight } : {}}
                                                    >
                                                        <div className="w-full h-full flex items-center justify-center py-[20px] px-6">
                                                            {content}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()
                    }
                </div>
            </div>
            <div className="absolute inset-0 pointer-events-none block lg:hidden">
                {showLeftButton && (
                    <button
                        className="absolute  hover:scale-105 transition-all duration-300 left-4 top-[40%] -translate-y-1/2 z-20 bg-[#fefefe] !rounded-full p-2 border-[6px] border-[#E5E4F9] shadow-[0_0_4px_1px_rgba(90,39,231,0.2)] pointer-events-auto"
                        onClick={() => scroll("left")}
                    >
                        <ChevronLeft size={20} />
                    </button>
                )}
                {showRightButton && (
                    <button
                        className="absolute hover:scale-105 transition-all duration-300 right-4 top-[40%] -translate-y-1/2 z-20 bg-[#fefefe] !rounded-full p-2 border-[6px] border-[#E5E4F9] shadow-[0_0_4px_1px_rgba(90,39,231,0.2)] pointer-events-auto"
                        onClick={() => scroll("right")}
                    >
                        <ChevronRight size={20} />
                    </button>
                )}
            </div>
        </div>
    );
}